/**
 * Apply gate — one rule for everyone.
 *
 * A professional can apply when the fields a hospital needs to act on an
 * application are present: full name and specialization — exactly what the
 * task-flow onboarding sheet collects, so users who finish onboarding pass
 * the gate naturally, with no bypass paths. Blocked users are sent to the
 * same onboarding sheet rather than shown a percentage.
 */

export interface ApplyGateProfile {
  full_name?: string | null;
}

export interface ApplyGateProfessional {
  specialization?: string | null;
}

const filled = (v: string | null | undefined) => !!v && v.toString().trim() !== "";

/** Human-readable labels of the required fields that are still empty. */
export function getMissingApplyFields(
  profile: ApplyGateProfile | null | undefined,
  professional: ApplyGateProfessional | null | undefined,
): string[] {
  const missing: string[] = [];
  if (!filled(profile?.full_name)) missing.push("full name");
  if (!filled(professional?.specialization)) missing.push("specialization");
  return missing;
}

/** "your full name" / "your full name and specialization" */
export function describeMissingApplyFields(missing: string[]): string {
  if (missing.length === 0) return "";
  if (missing.length === 1) return `your ${missing[0]}`;
  return `your ${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
}

export function canApply(missing: string[] | null): boolean {
  return missing !== null && missing.length === 0;
}

// ── Task-flow completion flag ────────────────────────────────────────────
// Set when the user finishes the minimal onboarding sheet mid-apply, so the
// resumed flow can proceed without waiting on a refetch. The gate above is
// the source of truth; this is a UX hint, not a bypass.

const APPLY_READY_KEY = "mb_apply_onboarding_ready";

function setPersisted(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
  sessionStorage.setItem(key, value);
}

function getPersisted(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return sessionStorage.getItem(key);
  }
}

function removePersisted(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  sessionStorage.removeItem(key);
}

const READY_TTL_MS = 24 * 60 * 60 * 1000;

export function markApplyOnboardingReady() {
  setPersisted(APPLY_READY_KEY, String(Date.now()));
}

export function isApplyOnboardingReady(): boolean {
  const raw = getPersisted(APPLY_READY_KEY);
  if (!raw) return false;
  const savedAt = Number(raw);
  // Legacy "1" or anything unparseable/expired is stale — clear it.
  if (!Number.isFinite(savedAt) || savedAt < 10_000 || Date.now() - savedAt > READY_TTL_MS) {
    removePersisted(APPLY_READY_KEY);
    return false;
  }
  return true;
}

export function clearApplyOnboardingReady() {
  removePersisted(APPLY_READY_KEY);
}
